import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AnimatedBackground from '../components/AnimatedBackground';
import { fireEvent } from '../utils/funnelEvents';
import ArticleStructuredData from '../components/ArticleStructuredData';
import VerticalHeader from '../components/VerticalHeader';

import ResearchersHero from './researchers/ResearchersHero';
import Workflow from './researchers/Workflow';
import PainPoints from './researchers/PainPoints';
import DocumentTypes from './researchers/DocumentTypes';
import ZoteroIntegration from './researchers/ZoteroIntegration';
import RelatedReading from './researchers/RelatedReading';
import VerticalPricing from './shared/VerticalPricing';
import ResearchersFAQ from './researchers/ResearchersFAQ';
import VerticalContact from './shared/VerticalContact';
import ResearchInviteCard from '../components/research/ResearchInviteCard';

const PAGE_TITLE = 'AI File Organizer for Researchers (Zotero, Academic PDFs) | Sortio';
const PAGE_DESCRIPTION =
  'Sortio renames academic PDFs by author and year, routes papers into project folders, and syncs to Zotero collections. Built for PhD students, postdocs, PIs, and labs with 5,000+ paper libraries. A fully local option (Ollama) keeps unpublished manuscripts on your machine.';

const NAV = [
  { href: '#workflow', label: 'How it works' },
  { href: '#document-types', label: 'Document types' },
  { href: '#zotero', label: 'Zotero' },
  { href: '#pricing', label: 'Pricing' },
];

const ResearchersPage = () => {
  useEffect(() => {
    fireEvent('team_page_view', { vertical: 'researchers' });
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="Zotero file organization, academic PDF organization, research file management AI, PhD literature review organization, rename PDFs by author year, research project folder, lab PDF library, Crossref enrichment"
        />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.getsortio.com/for-researchers" />
        <link rel="canonical" href="https://www.getsortio.com/for-researchers" />
      </Helmet>
      <ArticleStructuredData
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        datePublished="2026-05-14T00:00:00.000Z"
        dateModified="2026-05-14T00:00:00.000Z"
        authorName="Sortio Team"
      />

      <VerticalHeader verticalLabel="Researchers" navItems={NAV} />
      <ResearchersHero />

      <div className="relative">
        <AnimatedBackground variant="business" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <Workflow />
          <PainPoints />
          <DocumentTypes />
          <ZoteroIntegration />
          <RelatedReading />
          <VerticalPricing
            verticalLabel="Researchers"
            description="Solo PhD students often fit on Free for ongoing work after a one-time Pro month to clean the historical library. Labs and PIs graduate to Team for a shared rule library across students."
            proDescription="For PhD students, postdocs, and PIs"
            proFeatureLabel="Author/year/title rename + Zotero sync"
          />
          <ResearchersFAQ />
          <ResearchInviteCard vertical="academia" />
          <VerticalContact
            waitlistType="researchers"
            body="Tell us about your lab or project and we will show you Sortio working with a sample of your PDF library."
            orgLabel="Lab or Institution"
            orgPlaceholder="Smith Lab, University X"
            sizeLabel="Group Size"
            sizeOptions={[
              { value: 'solo', label: 'Solo researcher' },
              { value: '2-5', label: 'Small lab (2-5)' },
              { value: '6-20', label: 'Mid lab (6-20)' },
              { value: '20+', label: 'Large group (20+)' },
            ]}
          />
        </div>
      </div>
    </>
  );
};

export default ResearchersPage;
